<template>
  <div>
    <!-- UI-9: Search bar -->
    <div class="mb-3 d-flex gap-2 flex-wrap">
      <b-input-group style="max-width: 300px;">
        <b-input-group-prepend is-text>
          <b-icon icon="search"/>
        </b-input-group-prepend>
        <b-form-input v-model="searchQuery" placeholder="Поиск агентов..." @input="onSearch" debounce="300" aria-label="Поиск агентов"/>
      </b-input-group>
      <b-form-select v-model="statusFilter" :options="statusOptions" @change="fetchData" style="max-width: 180px;" aria-label="Фильтр по статусу"/>
      <b-button variant="dark" class="ml-auto" @click="$bvModal.show('agent-modal')">
        <b-icon icon="plus" /> Добавить агента
      </b-button>
    </div>

    <!-- DESIGN-3: Skeleton loading -->
    <skeleton v-if="loading" :rows="6" :cols="6"/>

    <!-- DESIGN-4: Empty state -->
    <empty-state
      v-if="!loading && data.length === 0"
      icon="pc-display"
      title="Агенты не установлены"
      description="Установите агент на удалённую машину для запуска сканирований в её сети"
      actionText="Создать агента"
      actionIcon="plus"
      @action="$bvModal.show('agent-modal')"
    />

    <b-table v-if="!loading && data.length > 0" striped borderless head-variant="dark" :fields="agentsFields" :items="filteredData" :filter="searchQuery" :filter-function="customFilter">
      <template #cell(status)="row">
        <b-badge :variant="row.item.status === 'online' ? 'success' : 'danger'" class="d-inline-flex align-items-center gap-1">
          <span :class="['status-dot', row.item.status === 'online' ? 'online' : 'offline']"></span>
          {{ row.item.status === 'online' ? 'В сети' : 'Не в сети' }}
        </b-badge>
      </template>
      <template #cell(name)="row">
        <span class="font-weight-bold" style="color: var(--text-foreground);">{{ row.item.name }}</span>
      </template>
      <template #cell(site_name)="row">
        <b-badge v-if="row.item.site_name" variant="info">{{ row.item.site_name }}</b-badge>
        <span v-else style="color: var(--text-muted);">—</span>
      </template>
      <template #cell(hostname)="row">
        <code style="color: var(--text-muted);">{{ row.item.hostname || '—' }}</code>
      </template>
      <template #cell(last_seen)="row">
        <span style="color: var(--text-muted);">{{ row.item.last_seen !== null ? row.item.last_seen.split('.', 1)[0].replace('T', ' ') : '—' }}</span>
      </template>
      <template #cell(actions)="row">
        <b-dropdown variant="outline" right>
          <template #button-content>
            <b-icon variant="dark" icon="three-dots-vertical"/>
          </template>
          <b-dropdown-item variant="dark" @click="selectedAgent = row.item" v-b-modal.agent-setup-modal>
            <b-icon icon="hdd-network"/>
            <label class="ml-1">Настройка</label>
          </b-dropdown-item>
          <b-dropdown-item variant="dark" @click="showAgentToken(row.item)">
            <b-icon icon="key"/>
            <label class="ml-1">Токен</label>
          </b-dropdown-item>
          <b-dropdown-item variant="danger" @click="selectedAgent = row.item" v-b-modal.delete-agent-modal>
            <b-icon icon="trash-fill"/>
            <label class="ml-1">Удалить</label>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>
    <pagination v-if="!loading && data.length > 0" :page="page" :limit="limit" :limits="limits" :total="total" name="agents" @pagination="pagination"/>
    <deletion id="delete-agent-modal" title="Удалить агента" removeWord="удалить" @deletion="deleteAgent" @clean="selectedAgent = null" v-if="selectedAgent !== null">
      <span>агента <strong>{{ selectedAgent.name }}</strong></span>
    </deletion>
    <agent id="agent-modal" @confirm="confirm" @created="onAgentCreated" @clean="selectedAgent = null"/>
    <agent-token id="agent-token-modal" :token="agentToken" @confirm="confirm"/>
    <agent-setup id="agent-setup-modal" :agent="selectedAgent" v-if="selectedAgent !== null" @saved="fetchData" @installed="fetchData"/>
  </div>
</template>

<script>
import RekonoApi from '@/backend/RekonoApi'
import Deletion from '@/common/Deletion'
import Pagination from '@/common/Pagination'
import Agent from '@/modals/Agent'
import AgentToken from '@/modals/AgentToken'
import AgentSetup from '@/modals/AgentSetup'
export default {
  name: 'agentsPage',
  mixins: [RekonoApi],
  data () {
    this.fetchData()
    return {
      data: [],
      loading: true,
      searchQuery: '',
      statusFilter: null,
      pollingInterval: null,
      agentsFields: [
        { key: 'name', label: 'Имя', sortable: true },
        { key: 'site_name', label: 'Сайт', sortable: true },
        { key: 'hostname', label: 'Хост', sortable: true },
        { key: 'platform', label: 'Платформа', sortable: true },
        { key: 'status', label: 'Статус', sortable: true },
        { key: 'last_seen', label: 'Последняя активность', sortable: true },
        { key: 'actions', label: '', sortable: false }
      ],
      selectedAgent: null,
      agentToken: null,
      filters: [],
      statusOptions: [
        { value: null, text: 'Все статусы' },
        { value: 'online', text: 'В сети' },
        { value: 'offline', text: 'Не в сети' }
      ]
    }
  },
  components: {
    Deletion,
    Pagination,
    Agent,
    AgentToken,
    AgentSetup
  },
  computed: {
    filteredData () {
      let result = this.data
      if (this.statusFilter === 'online') {
        result = result.filter(a => a.status === 'online')
      } else if (this.statusFilter === 'offline') {
        result = result.filter(a => a.status !== 'online')
      }
      return result
    }
  },
  mounted () {
    // UI-8: Real-time polling every 30 seconds
    this.pollingInterval = setInterval(() => {
      this.fetchData(true)
    }, 30000)
  },
  beforeDestroy () {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
    }
  },
  methods: {
    confirm (event) {
      if (event.reload) {
        this.fetchData()
      }
      this.$bvModal.hide(event.id)
    },
    fetchData (silent = false) {
      if (!silent) this.loading = true
      const params = { o: 'name' }
      if (this.statusFilter) params.status = this.statusFilter
      return this.getOnePage('/api/agents/', params)
        .then(response => {
          this.data = response.data.results
          this.total = response.data.count
        })
        .finally(() => { this.loading = false })
    },
    onSearch () {
      // Local filtering handled by customFilter
    },
    customFilter (item) {
      if (!this.searchQuery) return true
      const q = this.searchQuery.toLowerCase()
      return (item.name && item.name.toLowerCase().includes(q)) ||
             (item.hostname && item.hostname.toLowerCase().includes(q)) ||
             (item.platform && item.platform.toLowerCase().includes(q))
    },
    deleteAgent () {
      this.delete(`/api/agents/${this.selectedAgent.id}/`, this.selectedAgent.name, 'Агент успешно удалён').then(() => this.fetchData())
    },
    onAgentCreated (token) {
      this.agentToken = token
      this.$bvModal.show('agent-token-modal')
    },
    showAgentToken (agent) {
      this.selectedAgent = agent
      this.post(`/api/agents/${agent.id}/generate-token/`, null, agent.name, 'Новый токен сгенерирован')
        .then(data => {
          this.agentToken = data.token
          this.$bvModal.show('agent-token-modal')
        })
    }
  }
}
</script>

<style scoped>
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}
.status-dot.online {
  background-color: #22C55E;
  box-shadow: 0 0 4px #22C55E;
}
.status-dot.offline {
  background-color: #ef4444;
}
</style>